import { motion } from "framer-motion";
import { MapPin, Navigation, Mail, Clock4 } from "lucide-react";
import { CLINIC } from "../data/content";
import { Kicker } from "./fx";

const EASE = [0.22, 1, 0.36, 1];

export default function LocationSection() {
  return (
    <section
      id="visit"
      className="relative border-t border-white/10 bg-navy-deep py-24 sm:py-32"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 top-1/3 h-[360px] w-[360px] rounded-full bg-toothcyan/[0.06] blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-5"
        >
          <Kicker>Find The Clinic</Kicker>
          <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-5xl">
            In the heart of{" "}
            <em className="italic text-ember">Saheed Nagar.</em>
          </h2>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-4 rounded-3xl border border-white/10 bg-[#0F192C] p-5 transition-colors duration-300 hover:border-toothcyan/40">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ember/15 text-ember">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <div className="text-sm font-bold text-ivory">{CLINIC.name}</div>
                <p
                  data-testid="location-address-text"
                  className="mt-1 text-sm leading-relaxed text-mist"
                >
                  {CLINIC.address}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-3xl border border-white/10 bg-[#0F192C] p-5 transition-colors duration-300 hover:border-toothcyan/40">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ember/15 text-ember">
                <Clock4 className="h-5 w-5" />
              </span>
              <div className="text-sm leading-relaxed text-mist">
                <span className="font-semibold text-ivory">Mon – Sat:</span>{" "}
                11:00 AM – 08:00 PM
                <br />
                <span className="font-semibold text-ivory">Sunday:</span> On
                appointment basis
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-3xl border border-white/10 bg-[#0F192C] p-5 transition-colors duration-300 hover:border-toothcyan/40">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ember/15 text-ember">
                <Mail className="h-5 w-5" />
              </span>
              <div className="text-sm leading-relaxed text-mist">
                Appointment notifications &amp; enquiries
                <br />
                <a
                  data-testid="location-email-link"
                  href={`mailto:${CLINIC.email}`}
                  className="font-semibold text-toothcyan hover:underline"
                >
                  {CLINIC.email}
                </a>
              </div>
            </div>

            <a
              data-testid="location-google-maps-link"
              href={CLINIC.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-ember to-amber-500 px-7 py-4 text-sm font-bold text-[#070B12] shadow-[0_0_24px_rgba(232,178,92,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_38px_rgba(232,178,92,0.45)] active:scale-[0.98]"
            >
              <Navigation className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              Open in Google Maps
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{
            opacity: 0,
            clipPath: "polygon(0 8%, 100% 0%, 100% 92%, 0 100%)",
          }}
          whileInView={{ opacity: 1, clipPath: "polygon(0 0%, 100% 0%, 100% 100%, 0 100%)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.12, ease: EASE }}
          className="lg:col-span-7"
        >
          <div className="h-full rounded-[2.2rem] border border-white/10 bg-[#0F192C] p-2.5 shadow-[0_25px_70px_rgba(0,0,0,0.5)]">
            <div
              data-testid="location-map-embed"
              className="h-full min-h-[440px] overflow-hidden rounded-[1.8rem]"
            >
              <iframe
                title="Narayan Dental Care — Saheed Nagar, Bhubaneswar map"
                src="https://maps.google.com/maps?q=Narayan%20Dental%20Care%2C%20Saheed%20Nagar%2C%20Bhubaneswar%2C%20Odisha%20751007&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="h-full min-h-[440px] w-full grayscale-[25%] invert-[0.92] hue-rotate-180 contrast-[0.92] transition-all duration-700 hover:invert-0 hover:hue-rotate-0 hover:grayscale-0 hover:contrast-100"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
